import api from './api';
import fileDownload from 'js-file-download';

const fileService = {
  // Get all files
  getFiles: async (folderId = null) => {
    const params = folderId ? { folderId } : {};
    const response = await api.get('/files', { params });
    return response.data;
  },

  // ============================================
  // Upload file - FIXED for CORS and large files
  // ============================================
  uploadFile: async (file, folderId = null, onUploadProgress) => {
    try {
      console.log('📤 Starting upload:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');

      const formData = new FormData();
      formData.append('file', file);

      if (folderId) {
        formData.append('folderId', folderId);
      }

      // Log FormData contents
      console.log('📦 FormData contents:');
      for (let pair of formData.entries()) {
        console.log('  -', pair[0], ':', pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]);
      }

      const response = await api.post('/files/upload', formData, {
        timeout: 600000, // 10 minutes for large files
        headers: {
          // ✅ Don't set Content-Type - let browser set it with boundary
          // 'Content-Type': 'multipart/form-data' is set automatically
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = total > 0
              ? Math.round((progressEvent.loaded * 100) / total)
              : 0;

            console.log(`📊 Upload: ${percentCompleted}% (${(progressEvent.loaded / 1024 / 1024).toFixed(2)} MB / ${(total / 1024 / 1024).toFixed(2)} MB)`);
            onUploadProgress(percentCompleted);
          }
        },
      });

      console.log('✅ Upload complete:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },

  // ============================================
  // FIXED: Download file with progress - OPTIMIZED FOR LARGE FILES
  // ============================================
  downloadFile: async (fileId, filename, onDownloadProgress) => {
    try {
      console.log('📥 fileService.downloadFile:', { fileId, filename });

      // Try primary endpoint: /files/${fileId}/download
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob',
        timeout: 300000, // 5 minutes timeout for large files
        onDownloadProgress: (progressEvent) => {
          if (onDownloadProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const loaded = progressEvent.loaded;

            // Calculate percentage (handle cases where total is unknown)
            let percentCompleted = 0;
            if (total > 0 && progressEvent.total) {
              percentCompleted = Math.round((loaded * 100) / total);
            } else {
              // If total is unknown, estimate based on loaded bytes
              percentCompleted = Math.min(Math.round(loaded / 10000), 99); // Cap at 99%
            }

            console.log(`📊 Download: ${percentCompleted}% (${(loaded / 1024 / 1024).toFixed(2)} MB ${progressEvent.total ? '/ ' + (total / 1024 / 1024).toFixed(2) + ' MB' : ''})`);

            // Call progress callback - don't send 100% until file is saved
            if (percentCompleted < 100) {
              onDownloadProgress(percentCompleted, loaded, total);
            }
          }
        },
      });

      console.log('💾 Download stream complete, saving file...');

      // Get filename from response headers if not provided
      if (!filename) {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }

      // Use js-file-download to trigger download
      fileDownload(response.data, filename || 'download');

      // NOW send 100% after file is actually saved
      if (onDownloadProgress) {
        const finalSize = response.data.size || response.headers['content-length'];
        console.log('✅ File saved, sending 100% completion');
        onDownloadProgress(100, finalSize, finalSize);
      }

      console.log('✅ Download complete!');
      return response.data;

    } catch (error) {
      console.error('❌ Download error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status
      });

      // Fallback: Try alternative endpoint /files/download/${fileId}
      if (error.response && error.response.status === 404) {
        console.log('⚠️ Trying fallback endpoint...');
        try {
          const response = await api.get(`/files/download/${fileId}`, {
            responseType: 'blob',
            timeout: 300000,
            onDownloadProgress: (progressEvent) => {
              if (onDownloadProgress) {
                const total = progressEvent.total || progressEvent.loaded;
                const loaded = progressEvent.loaded;
                let percentCompleted = 0;

                if (total > 0 && progressEvent.total) {
                  percentCompleted = Math.round((loaded * 100) / total);
                } else {
                  percentCompleted = Math.min(Math.round(loaded / 10000), 99);
                }

                console.log(`📊 Download (fallback): ${percentCompleted}%`);

                if (percentCompleted < 100) {
                  onDownloadProgress(percentCompleted, loaded, total);
                }
              }
            },
          });

          if (!filename) {
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
              }
            }
          }

          fileDownload(response.data, filename || 'download');

          if (onDownloadProgress) {
            const finalSize = response.data.size || response.headers['content-length'];
            onDownloadProgress(100, finalSize, finalSize);
          }

          console.log('✅ Download complete (fallback)!');
          return response.data;
        } catch (fallbackError) {
          console.error('❌ Fallback download also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  },

  // Delete file
  deleteFile: async (fileId) => {
    const response = await api.delete(`/files/${fileId}`);
    return response.data;
  },

  // Rename file
  renameFile: async (fileId, newName) => {
    const response = await api.put(`/files/${fileId}`, { name: newName });
    return response.data;
  },

  // Search files
  searchFiles: async (query) => {
    const response = await api.get('/files/search', { params: { q: query } });
    return response.data;
  },

  // Get file details
  getFileDetails: async (fileId) => {
    const response = await api.get(`/files/${fileId}`);
    return response.data;
  },

  // Share file (generate public link)
  shareFile: async (fileId) => {
    const response = await api.post(`/files/${fileId}/share`);
    return response.data;
  },

  // Upload multiple files (batch upload)
  uploadFiles: async (files, folderId = null, onUploadProgress) => {
    try {
      const fileArray = Array.isArray(files) ? files : [files];
      console.log(`📤 Starting batch upload: ${fileArray.length} files`);

      const formData = new FormData();

      // Append all files
      fileArray.forEach(file => {
        formData.append('files', file);
        console.log(`  - ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      });

      if (folderId) {
        formData.append('folderId', folderId);
      }

      const response = await api.post('/files/upload', formData, {
        timeout: 600000, // 10 minutes for large files
        headers: {
          // Let browser set Content-Type with boundary
        },
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = total > 0
              ? Math.round((progressEvent.loaded * 100) / total)
              : 0;

            console.log(`📊 Batch Upload: ${percentCompleted}%`);
            onUploadProgress(percentCompleted);
          }
        },
      });

      console.log('✅ Batch upload complete:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Batch upload error:', error);
      throw error;
    }
  },

  // Move file to folder
  moveFile: async (fileId, folderId) => {
    const response = await api.post(`/files/${fileId}/move`, { folderId });
    return response.data;
  },

  // Get storage statistics
  getStorageStats: async () => {
    const response = await api.get('/files/storage');
    return response.data;
  },


};

export default fileService;